import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import { setClassNames } from '@ddc/classnames-js';
import { useMyCarsContentActions } from '../hooks/useMyCarsContentActions';
import { useIsChromeless } from '../hooks/useIsChromeless';
import MyCarsContent from './MyCarsContent';

function MyCarsModal({ isOpen, onModalClose, onExited }) {
	const myCarsData = useSelector((state) => state.myCarsData);
	const { deviceType } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const { welcome, onClickHandler } = useMyCarsContentActions(myCarsData);
	const labels = useLabels();
	const SHOPPING_ACTIVITY = labels.get('SHOPPING_ACTIVITY');
	// This component is only ever rendered when displayMode isn't
	// inline-content (Widget.jsx), so this is equivalent to isHeadlessModal
	// alone -- true for the drawer's header-icon popup, false for the
	// standalone (non-drawer) popup.
	const isChromeless = useIsChromeless();

	// Tags this popup's dialog -- only when a host like the drawer is
	// controlling it (inline-content never renders this component at all) --
	// so main.scss can paint its background from --mycars-panel-bg, matching
	// the text-color pairing in main.scss. The popup box sits directly under
	// <body>, not inside anything the drawer owns, so this tag is the only
	// hook a host has to reach its background.
	const modalDialogueClasses = [
		isDesktop ? 'mt-25 modal-lg' : 'pl-5',
		isChromeless && 'mycars-modal-dialog--chromeless'
	];

	const contentRef = useRef(null);
	const closeButtonRef = useRef(null);

	const headerClasses = [
		'border-bottom-0',
		'd-flex',
		'pb-0',
		!isDesktop
			? 'mt-1 pt-4 justify-content-between'
			: 'pt-3 pr-3 justify-content-end'
	];

	const handleKeyDown = (e) => {
		if (e.key !== 'Tab' || !contentRef.current) return;

		const focusableElements = [
			...contentRef.current.getLinkNodes(),
			contentRef.current.getShoppingActivityRef(),
			closeButtonRef.current
		].filter(Boolean);
		const currentFocusIndex = focusableElements.indexOf(
			document.activeElement
		);
		let nextFocusIndex = currentFocusIndex + (e.shiftKey ? -1 : 1);
		if (nextFocusIndex >= focusableElements.length) {
			nextFocusIndex = 0;
		} else if (nextFocusIndex < 0) {
			nextFocusIndex = focusableElements.length - 1;
		}
		e.preventDefault();
		focusableElements[nextFocusIndex]?.focus();
	};

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	const onOpen = () => {
		const primaryLinkNodes = contentRef.current.getLinkNodes();
		if (primaryLinkNodes?.length > 0) {
			primaryLinkNodes[0].focus();
		}
	};

	return (
		<Modal
			onHide={onModalClose}
			show={isOpen}
			onEntered={onOpen}
			onExited={onExited}
			className={!isDesktop ? 'dock-right' : ''}
			dialogClassName={setClassNames(modalDialogueClasses)}
			aria-labelledby="mycars-modal-title"
			aria-describedby="mycars-modal-title"
		>
			<div id="modal-content" className={setClassNames(headerClasses)}>
				{!isDesktop ? (
					<h3
						aria-level="2"
						id="mycars-modal-title"
						className="ddc-font-size-large pt-3 mt-2 pl-4 font-weight-normal modal-title"
					>
						{SHOPPING_ACTIVITY}
					</h3>
				) : null}
				<button
					ref={closeButtonRef}
					type="button"
					aria-label={labels.get('CLOSE_MODAL')}
					// cms-web's .text-link-muted sets a translucent black
					// (rgba(0,0,0,.6)) meant for a light modal background --
					// invisible against this popup's dark chromeless
					// background. Skipped when chromeless so the icon
					// inherits .modal-content's own color instead (main.scss
					// pairs it with the background there).
					className={setClassNames([
						'btn-unstyled',
						!isChromeless && 'text-link-muted',
						'py-3 px-4'
					])}
					onClick={() => onModalClose()}
				>
					<i
						className="ddc-icon ddc-icon-remove2 ddc-icon-size-large"
						aria-hidden="true"
					/>
				</button>
			</div>
			<div className={isDesktop ? 'px-6 pb-10 pt-1' : ''}>
				<MyCarsContent
					ref={contentRef}
					isMobile={!isDesktop}
					myCarsData={myCarsData}
					welcome={welcome}
					onClickHandler={onClickHandler}
				/>
			</div>
		</Modal>
	);
}

MyCarsModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onModalClose: PropTypes.func.isRequired,
	onExited: PropTypes.func
};

MyCarsModal.defaultProps = {
	onExited: undefined
};

export default MyCarsModal;
